import { Select, Tabs } from 'antd';
import { NewRoomModal } from 'components/Modals';
import PaymentFlowSelection from 'components/Modals/PaymentFlowSelection/PaymentFlowSelection';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { languagesValues } from 'routes/Main/Main';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setDataRoom } from 'store/slices/dataRoomSlice';
import { setSelectedKeys } from 'store/slices/windowStateSlice';
import classes from './TariffsComponent.module.scss';
import TariffsTable from './TariffsTable';
import api from 'api';
import {
  ITariff,
  Tariffs,
  TariffData,
  tariffsData,
  profileTariffs,
} from './tariffsData';

type TariffsComponentProps = {
  isProtected?: boolean;
};

export type ChoosePayment = {
  isOpenNewRoomModal: boolean;
  isChoosePaymentFlow?: boolean;
  tariff: ITariff | null;
};

const TariffsComponent = ({ isProtected }: TariffsComponentProps) => {
  const [dataSource, setDataSource] = useState<any>([]);
  const [forAYear] = useState(false);
  const [newRoomState, setNewRoomState] = useState<ChoosePayment>({
    isOpenNewRoomModal: false,
    isChoosePaymentFlow: false,
    tariff: null,
  });
  const [tariffMode, setTariffMode] = useState<'single' | 'quota'>('single');
  const [tariffsApiData, setTariffsApiData] = useState<TariffData[]>([]);
  const [isTariffsLoading, setIsTariffsLoading] = useState(true);
  const [roomConstraints, setRoomConstraints] = useState<{
    members_amount: number;
    total_document_size: number;
  } | null>(null);

  const dispatch = useAppDispatch();
  const ended_tariff_lifetime = localStorage.getItem('ended_tariff_lifetime');

  const { userData, isLoadingUserData, available_rooms } = useAppSelector(
    (store) => store.userData
  );

  const defaultLanguage = localStorage.getItem('language') || 'en';
  const { i18n, t } = useTranslation();

  const onLanguageChange = (value: string) => {
    localStorage.setItem('language', value);
    i18n.changeLanguage(value);
  };

  useEffect(() => {
    const fetchTariffs = async () => {
      setIsTariffsLoading(true);
      try {
        const response = await api.getTariffs();
        setTariffsApiData(response.data || []);
      } catch (error) {
        console.error('Error fetching tariffs:', error);
        setTariffsApiData([]);
      } finally {
        setIsTariffsLoading(false);
      }
    };

    fetchTariffs();
  }, []);

  useEffect(() => {
    if (ended_tariff_lifetime) {
      const targetRoom = available_rooms.find(room => room.id === ended_tariff_lifetime);
      if (targetRoom?.rooms_constraints) {
        setRoomConstraints({
          members_amount: targetRoom.rooms_constraints.members_amount || 0,
          total_document_size: targetRoom.rooms_constraints.total_document_size || 0,
        });
      } else {
        setRoomConstraints(null);
      }
    } else {
      setRoomConstraints(null);
    }
  }, [ended_tariff_lifetime, available_rooms]);

  useEffect(() => {
    dispatch(setSelectedKeys(['tariffs']));
    setDataSource(tariffsData);
    dispatch(setDataRoom(null));

    return () => {
      localStorage.removeItem('ended_tariff_lifetime');
    };
  }, [dispatch]);

  React.useEffect(() => {
    const targetRoom = available_rooms.find(
      (room) => room.id === ended_tariff_lifetime
    );

    if (Boolean(ended_tariff_lifetime) && Boolean(targetRoom)) {
      setNewRoomState({
        isOpenNewRoomModal: false,
        isChoosePaymentFlow: true,
        tariff: null,
      });
    } else {
      localStorage.removeItem('ended_tariff_lifetime');
    }
  }, [available_rooms, ended_tariff_lifetime]);

  const mapTariffDataToITariff = (tariffData: TariffData, tariffName: Tariffs): ITariff => {
    return {
      id: tariffData.id,
      tariff_name: tariffName,
      tariff_api_name: tariffData.name,
      tariff_cost: tariffData.base_cost,
      tariff_currency: tariffData.currency,
      tariff_duration: tariffData.duration,
      tariff_duration_unit: tariffData.duration_unit,
      tariff_constraints: {
        storage: tariffData.base_constraints.storage,
        members: tariffData.base_constraints.members,
        rooms: tariffData.base_constraints.rooms,
      },
      type: tariffData.type,
    };
  };

  const openRightModal = (tariffName: Tariffs, quotaMode?: string) => {
    let selectedTariff: ITariff | null = null;

    if (tariffsApiData.length > 0) {
      let tariffData;

      if (profileTariffs.includes(tariffName)) {
        tariffData = tariffsApiData.find(t => t.name === Tariffs.Profile && t.type === tariffName);
      } else if (tariffMode === 'quota' && quotaMode) {
        tariffData = tariffsApiData.find(t => t.name === tariffName && t.type === quotaMode);
      } else {
        tariffData = tariffsApiData.find(t => t.name === tariffName);
      }

      if (tariffData) {
        selectedTariff = mapTariffDataToITariff(tariffData, tariffName);
      }
    }

    setNewRoomState({
      isOpenNewRoomModal: true,
      isChoosePaymentFlow: false,
      tariff: selectedTariff,
    });
  };

  const closeModals = () => {
    setNewRoomState({
      isOpenNewRoomModal: false,
      isChoosePaymentFlow: false,
      tariff: null,
    });
  };

  const tabItems = [
    {
      key: 'single',
      label: t('Tariffs.tabs.single'),
      children: (
        <TariffsTable
          t={t}
          userData={userData}
          isLoadingUserData={isLoadingUserData}
          endedTariffLifetime={ended_tariff_lifetime}
          forAYear={forAYear}
          handleOpenModal={openRightModal}
          updateDataSource={setDataSource}
          dataSource={dataSource}
          mode="single"
          tariffsApiData={tariffsApiData}
          isTariffsLoading={isTariffsLoading}
          roomConstraints={roomConstraints}
        />
      ),
    },

    {
      key: 'quota',
      label: t('Tariffs.tabs.quota'),
      children: (
        <TariffsTable
          t={t}
          userData={userData}
          isLoadingUserData={isLoadingUserData}
          endedTariffLifetime={ended_tariff_lifetime}
          forAYear={forAYear}
          handleOpenModal={openRightModal}
          updateDataSource={setDataSource}
          dataSource={dataSource}
          mode="quota"
          tariffsApiData={tariffsApiData}
          isTariffsLoading={isTariffsLoading}
          roomConstraints={roomConstraints}
        />
      ),
    },
  ];

  return (
    <div className={isProtected ? classes.protectedWrap : classes.triffsWrap}>
      <div className={classes.headerWrap}>
        {/* 15535 Заккоментить функционал переключения оплаты за год */}
        {/* <div className={classes.periodWrap}>
          <span>{t(`Tariffs.forAMonth`)}</span>
          <Switch className={classes.switch} value={forAYear} onChange={setForAYear} />
          <span>{t(`Tariffs.forAYear`)}</span>
          <span className={classes.switchSale}>-10%</span>
        </div> */}
        {!isProtected && (
          <Select
            defaultValue={defaultLanguage}
            onChange={onLanguageChange}
            style={{ minWidth: 130 }}
            options={languagesValues}
            optionLabelProp={'label'}
          />
        )}
      </div>

      <Tabs
        defaultActiveKey='single'
        activeKey={tariffMode}
        onChange={(key) => setTariffMode(key as 'single' | 'quota')}
        items={tabItems}
        centered
        size='large'
      />

      <div style={{ height: 1 }}></div>

      {newRoomState.isOpenNewRoomModal && (
        <React.Suspense fallback={<div />}>
          <NewRoomModal
            open={newRoomState.isOpenNewRoomModal}
            onCancel={closeModals}
            newRoomState={newRoomState}
            mode={tariffMode}
          />
        </React.Suspense>
      )}

      {newRoomState.isChoosePaymentFlow && (
        <React.Suspense fallback={<div />}>
          <PaymentFlowSelection
            open={newRoomState.isChoosePaymentFlow}
            onCancel={closeModals}
          />
        </React.Suspense>
      )}
    </div>
  );
};

export default TariffsComponent;
